import type { ReactNode } from "react";

/**
 * The kit's button (SNIPPETS §5). Labels are verb + object: "Log weight", "End walk".
 *
 * When to use: every new button. `primary` is the one main action; `ghost` is its secondary;
 * `danger` names exactly what it removes. `size="lg"` (48) is for ActionBars and sheets, the
 * default (44) for inline use in a hero, Notice or row. A disabled button always has its reason in
 * words next to it — ActionBar does that for you.
 */
export function Button({
  children,
  onClick,
  variant = "primary",
  size = "md",
  grow,
  icon,
  disabled,
  busy,
  type = "button",
  describedBy,
  ariaLabel,
}: {
  children: ReactNode;
  onClick?: () => void;
  /** `text`: brand words on no fill — a row's trailing action ("+ glass", "Undo last") */
  variant?: "primary" | "ghost" | "danger" | "text";
  size?: "md" | "lg";
  /** take the free width of the row */
  grow?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  /** working: disabled, announced as busy, with a spinner in place of the icon */
  busy?: boolean;
  type?: "button" | "submit";
  describedBy?: string;
  ariaLabel?: string;
}) {
  const cls = [
    "k-btn",
    variant !== "primary" ? `k-btn--${variant}` : "",
    size === "lg" ? "k-btn--lg" : "",
    grow ? "k-btn--grow" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type={type}
      className={cls}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
    >
      {busy ? <span className="k-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

/**
 * A 44×44 icon-only button. The label is required: it is all a screen reader gets.
 *
 * When to use: a TopBar action, a Notice's dismiss, a player's side controls (`onSurface`).
 */
export function IconButton({
  icon,
  label,
  onClick,
  onSurface,
  className,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  /** on a player surface: --on-surface ink, never a token that inverts (invariant 10) */
  onSurface?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`k-iconbtn${onSurface ? " k-iconbtn--on-surface" : ""}${className ? " " + className : ""}`}
      onClick={onClick}
      aria-label={label}
    >
      {icon}
    </button>
  );
}
